import React, { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";
import { Modal } from "@material-ui/core";
import { GetAllSfCars } from "services/customers-services";
import { VinItem, SfCarItem } from "interfaces/customers.interface";
import { PRIMARY_TOYOTA } from "constants/style/color";
import { PRIMARY_REGULAR_FONT } from "constants/style/fonts";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  position: relative;
  width: 90vw;
  max-width: 950px;
  max-height: 85vh;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  font-family: ${PRIMARY_REGULAR_FONT};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: #212529;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0 0.5rem;

  &:hover {
    color: #000000;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const SectionContainer = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.8rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333333;
  border-left: 4px solid ${PRIMARY_TOYOTA};
  padding-left: 0.6rem;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }

  th {
    background-color: #f1f3f5;
    color: #495057;
    font-weight: 600;
  }

  tr:hover {
    background-color: #f8f9fa;
  }
`;

const EmptyMessage = styled.p`
  color: #6c757d;
  font-style: italic;
  margin: 0.5rem 0;
  font-size: 0.9rem;
`;

const LoadingText = styled.p`
  color: #0d6efd;
  font-weight: 500;
  margin: 0.5rem 0;
  font-size: 0.9rem;
`;

interface UserCarsModalProps {
  open: boolean;
  onClose: () => void;
  user: {
    id?: number;
    name?: string;
    vinList?: Array<VinItem>;
  } | null;
}

const UserCarsModal: React.FC<UserCarsModalProps> = ({
  open,
  onClose,
  user,
}): ReactElement | null => {
  const [sfCars, setSfCars] = useState<Array<SfCarItem>>([]);
  const [loadingSfCars, setLoadingSfCars] = useState<boolean>(false);
  const [errorSfCars, setErrorSfCars] = useState<string | null>(null);

  useEffect(() => {
    if (open && user?.id) {
      setLoadingSfCars(true);
      setErrorSfCars(null);
      GetAllSfCars(user.id)
        .then((res) => {
          const rawData: any = res?.data;
          let carsArray: Array<SfCarItem> = [];
          if (Array.isArray(rawData)) {
            carsArray = rawData;
          } else if (Array.isArray(rawData?.listEntityClass)) {
            carsArray = rawData.listEntityClass;
          } else if (Array.isArray(rawData?.data)) {
            carsArray = rawData.data;
          } else if (rawData && typeof rawData === "object") {
            carsArray = rawData.cars || rawData.carList || rawData.carlist || [];
          }
          setSfCars(carsArray);
          setLoadingSfCars(false);
        })
        .catch((err) => {
          console.error("Failed to fetch SF cars:", err);
          setErrorSfCars("Failed to load SF cars");
          setLoadingSfCars(false);
        });
    } else {
      setSfCars([]);
    }
  }, [open, user]);

  if (!open || !user) {
    return null;
  }

  const vinList = user.vinList || [];

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            Vehicle Details &mdash; {user.name || `User ID: ${user.id}`}
          </ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close modal">
            &times;
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {/* Section 1: User's Cars (vinList) */}
          <SectionContainer>
            <SectionTitle>1. User&apos;s Cars (Mobile Backend)</SectionTitle>
            {vinList.length > 0 ? (
              <StyledTable>
                <thead>
                  <tr>
                    <th>S.No.</th>
                    <th>Car Model</th>
                    <th>VIN Number</th>
                    <th>Registration No</th>
                    <th>SF Car ID</th>
                  </tr>
                </thead>
                <tbody>
                  {vinList.map((car, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{car.carModel || car.vinNumber || "N/A"}</td>
                      <td>{car.vinNumber || "N/A"}</td>
                      <td>{car.carRegistrationNo || "N/A"}</td>
                      <td>{car.sfCarId || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            ) : (
              <EmptyMessage>No cars associated in vinList array.</EmptyMessage>
            )}
          </SectionContainer>

          {/* Section 2: User's SF Cars */}
          <SectionContainer>
            <SectionTitle>2. User&apos;s SF Cars (Salesforce API)</SectionTitle>
            {loadingSfCars ? (
              <LoadingText>Loading Salesforce cars...</LoadingText>
            ) : errorSfCars ? (
              <EmptyMessage style={{ color: "#d9534f" }}>{errorSfCars}</EmptyMessage>
            ) : sfCars.length > 0 ? (
              <StyledTable>
                <thead>
                  <tr>
                    <th>S.No.</th>
                    <th>Car Model</th>
                    <th>VIN Number</th>
                    <th>Registration No</th>
                    <th>SF Car ID</th>
                  </tr>
                </thead>
                <tbody>
                  {sfCars.map((car, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{car.name || car.modelName || car.brandName || "N/A"}</td>
                      <td>{car.vinNumber || "N/A"}</td>
                      <td>{car.registrationNo || "N/A"}</td>
                      <td>{car.sfCarId || (car.id !== undefined && car.id !== 0 ? car.id : "N/A")}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            ) : (
              <EmptyMessage>No SF cars available</EmptyMessage>
            )}
          </SectionContainer>
        </ModalBody>
      </ModalContainer>
    </StyledModal>
  );
};

export default UserCarsModal;
